export const cacheTypes = {
	CUSTOMFIELD: 'CustomField',
	USER: 'User',
	ROLE: 'Role',
	ACTIVITYTYPE: 'ActivityType',
	APPOINTMENTTYPE: 'AppointmentType',
	ACCOUNTCATEGORY: 'AccountCategory',
	ACCOUNTCATEGORYTYPE: 'AccountCategoryType',
	CONTACTCATEGORY: 'ContactCategory',
	CONTACTCATEGORYTYPE: 'ContactCategoryType',
	LEADCHANNEL: 'Leadchannel',
	CAMPAIGN: 'campaign',
	PRODUCTCATEGORY: 'ProductCategory',
	PRODUCT: 'Product',
	DOCUMENTTEMPLATE: 'DocumentTemplate',
	ORDERSTAGE: 'OrderStage'
} as const;

export type Cache = {
	get: (key: string) => unknown;
	put: (key: string, value: unknown) => void;
	remove: () => void;
	removeAll: () => void;
};

type CacheEntry = { data: unknown; age: number };

const MAX_AGE = 3600000;

const caches = new Map<string, Map<string, CacheEntry>>();

/**
 * Port of `angular.copy` (1.3.20). Values cached here are `$resource` instances, which carry a
 * `$promise` whose resolved value points back at the instance — so shared and circular references
 * must resolve to a single copy or this recurses forever. Functions are passed by reference,
 * matching angular's `isObject` (which excludes them).
 */
const deepCopy = <T>(src: T, seen: Map<object, unknown> = new Map()): T => {
	if (src === null || typeof src !== 'object') {
		return src;
	}
	if (src instanceof Date) {
		return new Date(src.getTime()) as unknown as T;
	}
	if (src instanceof RegExp) {
		return new RegExp(src.source, src.flags) as unknown as T;
	}

	if (seen.has(src)) {
		return seen.get(src) as T;
	}

	if (Array.isArray(src)) {
		const copy: unknown[] = [];
		seen.set(src, copy);
		src.forEach(item => {
			copy.push(deepCopy(item, seen));
		});
		return copy as unknown as T;
	}

	const source = src as Record<string, unknown>;
	const copy = Object.create(Object.getPrototypeOf(source)) as Record<string, unknown>;
	seen.set(src, copy);
	Object.keys(source).forEach(key => {
		copy[key] = deepCopy(source[key], seen);
	});
	return copy as T;
};

export const getCache = (type: string): Cache => {
	let cache = caches.get(type);
	if (!cache) {
		cache = new Map<string, CacheEntry>();
		caches.set(type, cache);
	}
	const entries = cache;

	return {
		get: key => {
			const cached = entries.get(key);
			if (!cached) {
				return undefined;
			}

			if (Date.now() - cached.age > MAX_AGE) {
				return undefined;
			}

			return deepCopy(cached.data);
		},

		put: (key, value) => {
			entries.set(key, { data: deepCopy(value), age: Date.now() });
		},

		// The angular service declared `remove` without a key parameter, so the key its callers
		// pass (campaign.js) is dropped and nothing is evicted. Kept as-is on purpose.
		remove: () => undefined,

		removeAll: () => {
			entries.clear();
		}
	};
};

/**
 * Empties every cache in place. Consumers capture the accessor returned by `getCache` once and
 * reuse it across clears, so the inner maps must be emptied rather than replaced or dropped.
 */
export const clear = () => {
	caches.forEach(entries => {
		entries.clear();
	});
};

export default { cacheTypes, getCache, clear, restore: clear };
